<template>
  <div class="upload-filters-panel bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
    <button
      type="button"
      class="upload-filters-toggle"
      :aria-expanded="areFiltersOpen"
      aria-controls="upload-product-filters-form"
      @click="areFiltersOpen = !areFiltersOpen"
    >
      <span class="flex items-center gap-2">
        <i class="fas fa-filter text-gray-600" aria-hidden="true"></i>
        <span class="font-semibold text-gray-900">Filtres</span>
        <span
          v-if="activeFiltersCount"
          class="rounded-full bg-gray-800 px-2 py-0.5 text-xs font-bold text-white"
        >
          {{ activeFiltersCount }}
        </span>
      </span>
      <span class="flex items-center gap-3 text-sm text-gray-600">
        {{ totalProduits }} {{ totalProduits > 1 ? 'produits' : 'produit' }}
        <i
          class="fas fa-chevron-down text-xs transition-transform"
          :class="areFiltersOpen ? 'rotate-180' : ''"
          aria-hidden="true"
        ></i>
      </span>
    </button>

    <form
      id="upload-product-filters-form"
      @submit.prevent="handleSubmit"
      class="upload-filters-form space-y-3"
      :class="{ 'is-open': areFiltersOpen }"
    >
      <!-- Ligne unique compacte -->
      <div class="flex flex-wrap items-end gap-3">
        <!-- Référence -->
        <div class="filter-control flex-1 min-w-[200px]">
          <input
            v-model.trim="filters.reference"
            type="text"
            placeholder="🔍 Rechercher une référence..."
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <!-- ID Shooting -->
        <div class="filter-control w-32">
          <input
            v-model.trim="filters.idShooting"
            type="text"
            placeholder="ID Shooting"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>


        <!-- Filtre Photos -->
        <div class="filter-control w-40">
          <select
            v-model="filters.hasPhotos"
            class="w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">Toutes</option>
            <option value="with">Avec photos</option>
            <option value="without">Sans photos</option>
          </select>
        </div>

        <!-- Catégorie -->
        <div class="filter-control w-48">
          <select
            v-model="filters.categorie"
            class="w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">Catégorie</option>
            <option
              v-for="option in categoriesOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- Sous-catégorie -->
        <div class="filter-control w-48">
          <select
            v-model="filters.sousCategorie"
            :disabled="!filters.categorie"
            class="w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
          >
            <option value="">Sous-catégorie</option>
            <option
              v-for="option in sousCategoriesOptionsFiltered"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- Sous-sous-catégorie -->
        <div class="filter-control w-52">
          <select
            v-model="filters.sousSousCategorie"
            :disabled="!filters.sousCategorie"
            class="w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
          >
            <option value="">Sous-sous-catégorie</option>
            <option
              v-for="option in sousSousCategoriesOptionsFiltered"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- Bouton Réinitialiser -->
        <button
          type="button"
          @click="resetFilters"
          class="px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          title="Réinitialiser"
        >
          <i class="fas fa-undo"></i>
        </button>
      </div>

      <!-- Tri -->
      <div class="flex items-center gap-3 pt-1">
        <label class="text-xs font-medium text-gray-700">Trier par</label>
        <div class="flex gap-2">
          <button
            type="button"
            :class="[
              'px-3 py-1 text-xs font-medium rounded border transition-colors',
              filters.tri === 'date-desc' || filters.tri === 'date-asc'
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            ]"
            @click="filters.tri = 'date-desc'"
          >
            <i class="fas fa-calendar-alt mr-1"></i>Date
          </button>
          <button
            type="button"
            :class="[
              'px-3 py-1 text-xs font-medium rounded border transition-colors',
              filters.tri === 'reference-asc' || filters.tri === 'reference-desc'
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            ]"
            @click="filters.tri = 'reference-asc'"
          >
            <i class="fas fa-sort-alpha-down mr-1"></i>Alphabet
          </button>
        </div>
      </div>

      <!-- Total -->
      <div class="border-t pt-3 text-sm font-semibold text-gray-700 flex items-center gap-3">
        <span>{{ totalProduits }} {{ totalProduits > 1 ? 'produits' : 'produit' }}</span>
        <button
          type="button"
          @click="handleShowAllWithReset"
          class="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          title="Voir tous les uploads"
        >

          Voir tout
        </button>
        <button
          type="button"
          @click="setPremelFilter('en_ligne')"
          :class="[
            'px-2 py-1 text-xs rounded border transition-colors',
            filters.premelFilter === 'en_ligne'
              ? 'bg-green-600 text-white border-green-600'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          ]"
          title="Afficher uniquement les produits en ligne"
        >
          En ligne
        </button>
        <button
          type="button"
          @click="setPremelFilter('brouillon')"
          :class="[
            'px-2 py-1 text-xs rounded border transition-colors',
            filters.premelFilter === 'brouillon'
              ? 'bg-orange-500 text-white border-orange-500'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          ]"
          title="Afficher uniquement les produits en brouillon"
        >
          Brouillon
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import type { UploadProductFiltersData, UploadProductFiltersProps } from '@/types'

const props = withDefaults(defineProps<UploadProductFiltersProps & { initialFilters?: UploadProductFiltersData }>(), {
  totalProduits: 0,
  categories: () => [],
  sousCategories: () => [],
  sousSousCategories: () => [],
  categoriesTree: () => [],
  initialFilters: () => ({
    reference: '',
    idShooting: '',
    tri: 'date-desc',
    categorie: '',
    sousCategorie: '',
    sousSousCategorie: '',
    hasPhotos: '',
    premelFilter: ''
  })
})

const emit = defineEmits<{
  (e: 'filter', filters: UploadProductFiltersData): void
  (e: 'showAll'): void
}>()

// État des filtres (initialisé à partir des props afin de refléter la persistance)
const filters = ref<UploadProductFiltersData>({ ...props.initialFilters })
const areFiltersOpen = ref(false)

const activeFiltersCount = computed(() => {
  const values = [
    filters.value.reference,
    filters.value.idShooting,
    filters.value.categorie,
    filters.value.sousCategorie,
    filters.value.sousSousCategorie,
    filters.value.hasPhotos,
    filters.value.premelFilter,
  ]
  return values.filter(Boolean).length + (filters.value.tri !== 'date-desc' ? 1 : 0)
})

// Mettre à jour si le parent change l'état initial (ex: restauré depuis Pinia)
watch(() => props.initialFilters, (nf) => {
  if (!nf) return
  filters.value = { ...nf }
}, { deep: true })

// Catégories à 3 niveaux basées sur props.categoriesTree (comme ProductSheetModal)
const categoriesOptions = computed(() => {
  // Si categoriesTree est fourni, l'utiliser
  if (props.categoriesTree && props.categoriesTree.length > 0) {
    return props.categoriesTree.map(cat => ({
      value: String(cat.id_categorie),
      label: cat.label
    }))
  }
  // Fallback sur l'ancien système
  return props.categories
})

const sousCategoriesOptionsFiltered = computed(() => {
  // Si categoriesTree est fourni, naviguer dans l'arbre
  if (props.categoriesTree && props.categoriesTree.length > 0) {
    if (!filters.value.categorie) return []
    const catId = parseInt(filters.value.categorie)
    const found = props.categoriesTree.find(c => c.id_categorie === catId)
    if (!found || !found.children) return []
    return found.children.map(cat => ({
      value: String(cat.id_categorie),
      label: cat.label
    }))
  }
  // Fallback sur l'ancien système
  if (!filters.value.categorie) {
    return props.sousCategories
  }
  return props.sousCategories.filter(option => option.parentId === filters.value.categorie)
})

const sousSousCategoriesOptionsFiltered = computed(() => {
  // Si categoriesTree est fourni, naviguer dans l'arbre
  if (props.categoriesTree && props.categoriesTree.length > 0) {
    if (!filters.value.categorie || !filters.value.sousCategorie) return []
    const catId = parseInt(filters.value.categorie)
    const sousCatId = parseInt(filters.value.sousCategorie)
    const level1 = props.categoriesTree.find(c => c.id_categorie === catId)
    if (!level1 || !level1.children) return []
    const level2 = level1.children.find(c => c.id_categorie === sousCatId)
    if (!level2 || !level2.children) return []
    return level2.children.map(cat => ({
      value: String(cat.id_categorie),
      label: cat.label
    }))
  }
  // Fallback sur l'ancien système
  if (!filters.value.sousCategorie) {
    return props.sousSousCategories
  }
  return props.sousSousCategories.filter(option => option.parentId === filters.value.sousCategorie)
})

const setPremelFilter = (value: '' | 'en_ligne' | 'brouillon') => {
  filters.value.premelFilter = value
}

const handleShowAllWithReset = () => {
  filters.value.premelFilter = ''
  handleSubmit()
  emit('showAll')
}

// Soumettre les filtres
const handleSubmit = () => {
  emit('filter', { ...filters.value })
}

// Réinitialiser les filtres
const resetFilters = () => {
  filters.value = {
    reference: '',
    idShooting: '',
    tri: 'date-desc',
    categorie: '',
    sousCategorie: '',
    sousSousCategorie: '',
    hasPhotos: '',
    premelFilter: ''
  }
  handleSubmit()
}

// Debounce server-side filtering to avoid excessive requests and improve UX (like ProductFilters)
let filterDebounceTimer: number | undefined

// Watch sur référence pour filtrage en temps réel avec debounce
watch(() => filters.value.reference, () => {
  if (filterDebounceTimer) window.clearTimeout(filterDebounceTimer)
  filterDebounceTimer = window.setTimeout(() => {
    handleSubmit()
  }, 400)
})

watch(() => filters.value.idShooting, () => {
  if (filterDebounceTimer) window.clearTimeout(filterDebounceTimer)
  filterDebounceTimer = window.setTimeout(() => {
    handleSubmit()
  }, 400)
})

watch(() => filters.value.tri, () => {
  handleSubmit()
})

watch(() => filters.value.hasPhotos, () => {
  handleSubmit()
})

watch(() => filters.value.premelFilter, () => {
  handleSubmit()
})

watch(() => filters.value.categorie, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    filters.value.sousCategorie = ''
    filters.value.sousSousCategorie = ''
    handleSubmit()
  }
})

watch(() => filters.value.sousCategorie, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    filters.value.sousSousCategorie = ''
    handleSubmit()
  }
})

watch(() => filters.value.sousSousCategorie, () => {
  handleSubmit()
})

</script>

<style scoped>
.upload-filters-toggle {
  display: flex;
  width: 100%;
  min-height: 2.75rem;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  text-align: left;
}

.upload-filters-form {
  display: none;
}

.upload-filters-form.is-open {
  display: block;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #d1d5db;
}

select:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
}

@media (max-width: 1024px) {
  .upload-filters-panel {
    margin-bottom: 0.75rem;
    padding: 0.75rem;
  }

  .filter-control {
    flex: 1 1 100%;
    width: 100%;
    min-width: 0;
  }

  .upload-filters-form > div:last-child {
    flex-wrap: wrap;
  }
}
</style>
